/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import { FC, useCallback, useState } from 'react'
import { removeVideo, removeVideoVariables } from '__generatedTypes__/removeVideo'
import { updateVideo, updateVideoVariables } from '__generatedTypes__/updateVideo'
import { Revision, Revisions } from 'components/Revisions'
import client from 'configs/apolloClient'
import { FIND_ONE_VIDEOS, REMOVE_VIDEO, UPDATE_VIDEO } from 'queries/video'
import { findOneVideoCategory_findOneVideoCategory_video as IVideo } from '__generatedTypes__/findOneVideoCategory'
import { MembershipType } from '__generatedTypes__/globalTypes'
import { useHistory } from 'hooks/useHistory'
import { toast } from 'utils/toast'
import * as commonClasses from '../commonStyles'
import { IVideoCategoryProps, IVideoData } from '../types'
import withVideoCategoryContentCheck from '../withVideoCategoryContentCheck'
import VideoContentForm from './VideoContentForm'

const VideoContentEdit: FC<IVideoCategoryProps> = ({ videoCategory, content, redirectToVideoDashboard }) => {
  const { populateHistoryWithUser } = useHistory()
  const [initialsState] = useState<Omit<IVideo, '__typename'> & { membershipLevel: { id: MembershipType | null } }>({
    ...content,
    membershipLevel: { id: content.membershipLevel },
  })

  const [updateVideo] = useMutation<updateVideo, updateVideoVariables>(UPDATE_VIDEO)
  const [removeVideo] = useMutation<removeVideo, removeVideoVariables>(REMOVE_VIDEO)

  //TODO Add error handling
  const handleSubmit = useCallback(
    async (fieldsValues: IVideoData, videoLengthSec: number) => {
      const videoUpdate = {
        id: content.id,
        title: fieldsValues.title,
        link: fieldsValues.link,
        membershipLevel: fieldsValues.membershipLevel.id,
        videoLength: videoLengthSec,
        tags: fieldsValues.tags ?? [],
        history: populateHistoryWithUser(content.history),
      }

      try {
        const { data } = await updateVideo({
          variables: {
            updateVideoInputFieldName: videoUpdate,
          },
        })
        client.writeQuery({
          query: FIND_ONE_VIDEOS,
          variables: { findOneVideoId: content.id },
          data: {
            findOneVideo: { ...content, ...videoUpdate },
          },
        })

        if (data?.updateVideo.id) {
          redirectToVideoDashboard()
          toast.success('Video updated successfully')
        } else {
          toast.error('Failed to update video')
        }
      } catch (error) {
        toast.error('Failed to update video')
      }
    },
    [content, populateHistoryWithUser]
  )

  const handleRemoveResource = useCallback(async () => {
    try {
      const { data } = await removeVideo({
        variables: {
          removeVideoId: content.id,
        },
      })

      if (data?.removeVideo) {
        redirectToVideoDashboard()
        toast.success('Video removed successfully')
      } else {
        toast.error('Failed to remove video')
      }
    } catch (error) {
      toast.error('Failed to remove video')
    }
  }, [content])

  return (
    <div css={commonClasses.containerPadding}>
      <VideoContentForm
        videoCategory={videoCategory}
        handleSubmit={handleSubmit}
        initialValues={initialsState}
        onDeleteClick={handleRemoveResource}
      />
      <Revisions css={commonClasses.containerMargin} revisions={(content?.history ?? []) as Revision[]} />
    </div>
  )
}

export default withVideoCategoryContentCheck(VideoContentEdit)
